import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Search, User, LogOut, Shield } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/news", label: "News", id: "news" },
  { to: "/categories", label: "Categories", id: "categories" },
  { to: "/community", label: "Community", id: "community" },
];

function MediaCryptoLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" fill="#facc15"/>
      <polyline points="4,22 10,12 16,18 22,8 28,14" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="28" cy="14" r="2" fill="black"/>
    </svg>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/news?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-xl bg-black/70"
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <MediaCryptoLogo />
          <span className="font-display font-bold text-xl tracking-tight text-white">
            Media<span className="text-yellow-400">Crypto</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.id}-link`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide uppercase transition-colors link-underline ${
                  isActive ? "text-yellow-400" : "text-zinc-300 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden md:flex items-center bg-zinc-900/70 border border-white/10 px-3 h-9 rounded-none focus-within:border-yellow-400">
            <Search className="w-4 h-4 text-zinc-500 mr-2" />
            <input
              data-testid="nav-search-input"
              type="text"
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="bg-transparent outline-none text-sm w-40 placeholder:text-zinc-500"
            />
          </form>

          {user ? (
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="hidden md:flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-yellow-400 border border-yellow-400/30 px-3 py-2 hover:bg-yellow-400/10 transition"
                >
                  <Shield className="w-3 h-3" /> Admin
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu-trigger"
                    className="w-9 h-9 bg-yellow-400 text-black font-bold rounded-none flex items-center justify-center hover:bg-yellow-500 transition"
                  >
                    {(user.name || user.email)[0].toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white rounded-none w-56 z-[60]">
                  <DropdownMenuLabel className="font-mono text-xs text-zinc-500">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile-link" className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link-dd" className="cursor-pointer text-yellow-400">
                      <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={logout} data-testid="nav-logout-button" className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/login" data-testid="nav-login-link">
                <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                  Sign in
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-none font-bold uppercase tracking-wide">
                  Join
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}